// src/features/TechnicianAnalysis/IssueGallery.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { useI18n } from '../../i18n/i18n';
import { getReportsWithPhotos, getReportsByIssueType } from '../../services/autoArchiveService';

const IssueGallery = ({ user }) => {
  const { t } = useI18n();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [issueFilter, setIssueFilter] = useState('all');

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm, issueFilter]);

  const loadReports = async () => {
    const reportsWithPhotos = await getReportsWithPhotos();
    setReports(reportsWithPhotos);
  };

  const filterReports = () => {
    let filtered = reports;

    if (searchTerm) {
      filtered = filtered.filter(report =>
        report.createdBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (report.photos && report.photos.some(photo => 
          photo.description?.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
    }

    if (issueFilter !== 'all') {
      filtered = filtered.filter(report =>
        report.issueTypes && report.issueTypes.includes(issueFilter)
      );
    }

    setFilteredReports(filtered);
  };

  const getAllIssueTypes = () => {
    const issueTypes = new Set();
    reports.forEach(report => {
      if (report.issueTypes) {
        report.issueTypes.forEach(issue => issueTypes.add(issue));
      }
    });
    return Array.from(issueTypes);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('issue_gallery_title')}
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        {t('issue_gallery_subtitle')}
      </Typography>

      {/* Фільтри */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('search_photos')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('search_by_technician_or_description')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('filter_by_issue')}</InputLabel>
                <Select
                  value={issueFilter}
                  label={t('filter_by_issue')}
                  onChange={(e) => setIssueFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('all_issues')}</MenuItem>
                  {getAllIssueTypes().map(issue => (
                    <MenuItem key={issue} value={issue}>
                      {t(issue) || issue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Галерея */}
      <Grid container spacing={3}>
        {filteredReports.map((report) => (
          <Grid item xs={12} key={report.id}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h6">{report.id}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('by')} {report.createdBy} • {new Date(report.date).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {report.issueTypes && report.issueTypes.map(issue => (
                      <Chip
                        key={issue}
                        label={t(issue) || issue}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Stack>

                {/* Фото звіту */}
                <Grid container spacing={2}>
                  {report.photos && report.photos.map((photo, index) => (
                    <Grid item xs={12} sm={6} md={4} key={photo.id || index}>
                      <Card variant="outlined">
                        <Box
                          sx={{
                            height: 200,
                            overflow: 'hidden',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(photo.url, '_blank')}
                        >
                          <img
                            src={photo.url}
                            alt={photo.description || `Photo ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </Box>
                        <CardContent sx={{ p: 2 }}>
                          {photo.description && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {photo.description}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {photo.timestamp ? new Date(photo.timestamp).toLocaleString() : ''}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredReports.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {t('no_photos_found')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('no_photos_found_desc')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default IssueGallery;